import api from './api';
import { Course, Lesson, CourseSearchParams, PaginatedResponse } from '../types';

export const coursesService = {
  async getAll(): Promise<Course[]> {
    const response = await api.get('/courses');
    return response.data;
  },

  async getFeatured(): Promise<Course[]> {
    const response = await api.get('/courses/featured');
    return response.data;
  },

  async search(params: CourseSearchParams): Promise<PaginatedResponse<Course>> {
    const response = await api.get('/courses/search', { params });
    return response.data;
  },

  async getCategories(): Promise<string[]> {
    const response = await api.get('/courses/categories');
    return response.data;
  },

  async getBySlug(slug: string): Promise<{ course: Course; lessons: Lesson[] }> {
    const response = await api.get(`/courses/${slug}`);
    return response.data;
  },

  async getMyCourses(): Promise<Course[]> {
    const response = await api.get('/user/courses');
    return response.data;
  },

  async getCourseProgress(courseId: string): Promise<{ lessons: Lesson[]; progress: number }> {
    const response = await api.get(`/user/courses/${courseId}/progress`);
    return response.data;
  },

  async completeLesson(lessonId: string): Promise<{ progress: number }> {
    const response = await api.post(`/user/lessons/${lessonId}/complete`);
    return response.data;
  },

  async uncompleteLesson(lessonId: string): Promise<{ progress: number }> {
    const response = await api.delete(`/user/lessons/${lessonId}/complete`);
    return response.data;
  },
};

export const adminCoursesService = {
  async getAll(): Promise<Course[]> {
    const response = await api.get('/admin/courses');
    return response.data;
  },

  async create(course: Partial<Course>): Promise<Course> {
    const response = await api.post('/admin/courses', course);
    return response.data;
  },

  async update(id: string, course: Partial<Course>): Promise<Course> {
    const response = await api.put(`/admin/courses/${id}`, course);
    return response.data;
  },

  /** Backfill the course's access-expiry config onto its existing enrollments. */
  async applyAccessExpiry(
    id: string,
    overwrite = true
  ): Promise<{ total: number; updated: number; skipped: number; configured: boolean }> {
    const response = await api.post(`/admin/courses/${id}/apply-access-expiry`, { overwrite });
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/admin/courses/${id}`);
  },

  async getLessons(courseId: string): Promise<Lesson[]> {
    const response = await api.get(`/admin/courses/${courseId}/lessons`);
    return response.data;
  },

  async createLesson(courseId: string, lesson: Partial<Lesson>): Promise<Lesson> {
    const response = await api.post(`/admin/courses/${courseId}/lessons`, lesson);
    return response.data;
  },

  async updateLesson(courseId: string, lessonId: string, lesson: Partial<Lesson>): Promise<Lesson> {
    const response = await api.put(`/admin/courses/${courseId}/lessons/${lessonId}`, lesson);
    return response.data;
  },

  async deleteLesson(courseId: string, lessonId: string): Promise<void> {
    await api.delete(`/admin/courses/${courseId}/lessons/${lessonId}`);
  },
};
